import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo } from 'react';
import { ServerTable } from 'shared/ui/ServerTable';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { useGetCurrencySymbol } from 'app/appState';
import { ExportEntity, ExportModal, getIncomeStatisticsColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { BoxOccupancyObject, useGetIncomeStatisticsQuery } from 'entities/Warehouse';
import { useTableFilterContext } from 'features/TableFilter';
import { ServerTableActions } from 'shared/ui/ServerTable/types';
import { IncomeStatisticsObject } from 'entities/Warehouse/model/types';
import { transformIncomeStatisticsFilters } from '../utils/transformIncomeStatisticsFilters';
import { IncomeStatisticsFilter } from './IncomeStatisticsFilter';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

export const IncomeStatisticsTable: FC = memo(() => {
  const currencySymbol = useGetCurrencySymbol();

  const { openExportModal } = useOpenExportModal();

  const { t } = useAppTranslation(['contracts', 'common']);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const columns = useMemo<ColumnsType<IncomeStatisticsObject>>(
    () => [
      {
        title: t('Date'),

        key: 'date',
        render: (_, record) => <div className="font-semibold">{record.date}</div>,
      },
      {
        title: t('Rent amount'),

        key: 'rentAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.rentAmount)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Rent credited'),

        key: 'rentCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.rentCredited)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Insurance amount'),

        key: 'insuranceAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.insuranceAmount)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Insurance credited'),

        key: 'insuranceCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.insuranceCredited)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Deposit amount'),

        key: 'depositAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.depositAmount)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Deposit credited'),

        key: 'depositCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.depositCredited)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Other'),

        key: 'otherAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.otherAmount)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Other credited'),

        key: 'otherCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.otherCredited)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('VAT'),

        key: 'vatAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.vatAmount)} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('VAT credited'),

        key: 'vatCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {formatPrice(record.vatCredited)} {currencySymbol}
          </div>
        ),
      },
    ],
    [t, currencySymbol],
  );

  const tableActions: Array<ServerTableActions<BoxOccupancyObject>> = useMemo(
    () => [
      {
        name: t('Export', { ns: 'common' }),
        theme: 'secondary',
        icon: <ExportIcon />,
        iconPosition: 'prev',
        onClick: () => {
          openExportModal({
            filters: transformIncomeStatisticsFilters(appliedFilters),
            columns: getIncomeStatisticsColumnKeys(t),
            entity: ExportEntity.INCOME_STATISTICS,
          });
        },
      },
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [appliedFilters, openExportModal, setFiltersOpened, t],
  );

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7 font-tektur">{t('Statistics')}</div>
      <ServerTable
        columns={columns}
        fetchData={useGetIncomeStatisticsQuery}
        rowKey="date"
        tableActionsPosition="prev"
        tableActions={tableActions}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={transformIncomeStatisticsFilters(appliedFilters)}
      />
      <ExportModal />
      <IncomeStatisticsFilter />
    </div>
  );
});
